import React from 'react';
import { useIsPhone } from './useIsPhone';

// A desktop table that becomes a list of cards on a phone (docs/PHONE-LAYOUT.md).
// Usage: <PhoneCards rows={rows} card={r => <…/>}>{the table}</PhoneCards>
// Above md the children render untouched; below md each row renders through `card`.
// Rows with a stable id give a stable key; otherwise the index is used. Claude, 2026-09-28.
export function PhoneCards<T>({ rows, card, children, keyOf, empty }: {
  rows: T[];
  card: (row: T, i: number) => React.ReactNode;
  children: React.ReactNode;
  keyOf?: (row: T, i: number) => string | number;
  empty?: React.ReactNode;
}) {
  const phone = useIsPhone();
  if (!phone) return <>{children}</>;
  if (!rows.length) return <>{empty ?? children}</>;
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={keyOf ? keyOf(r, i) : i} className="rounded-xl border border-gray-800 bg-[#1a1d27] px-3 py-2.5 text-sm">
          {card(r, i)}
        </div>
      ))}
    </div>
  );
}

/** One "label: value" line inside a card. */
export function CardLine({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-xs py-0.5">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className="text-gray-200 text-right break-words min-w-0">{children}</span>
    </div>
  );
}
